/**
 * Umami Analytics Tracking Module
 *
 * Provides type-safe event tracking for Umami analytics.
 * Events can be tracked via:
 * 1. Data attributes on elements (data-umami-event="event-name")
 * 2. Programmatic calls to trackEvent()
 */

declare global {
  interface Window {
    umami?: {
      track: (eventName: string, eventData?: Record<string, unknown>) => void;
      identify: (data: { id: string } | Record<string, unknown>) => void;
    };
  }
}

export type UmamiEvent =
  // Lobby/Home Events
  | "create-lobby-hero"
  | "join-lobby-hero"
  | "duels-hero"
  | "copy-lobby-code"
  | "home-link"

  // Game Settings Events
  | "game-start"
  | "game-end"
  | "game-duration-change"
  | "allow-countries-select"
  | "allow-countries-unselect"
  | "set-start-article"
  | "set-target-article"
  | "change-start-article"
  | "remove-target-article"
  | "suggest-article"
  | "random-article-search"

  // Player Management Events
  | "kick-player-execute"

  // Daily Challenge Events
  | "daily-challenge-hero"
  | "daily-challenge-start"
  | "daily-challenge-give-up"
  | "daily-challenge-share-results"
  | "daily-challenge-play-another-game"
  | "daily-challenge-leaderboard-tab"
  | "daily-challenge-timeline-tab"
  | "daily-challenge-summary-tab"

  // Results/Scoreboard Events
  | "results-scoreboard-tab"
  | "results-targets-tab"
  | "results-paths-tab"

  // OpenLobby Events
  | "openlobby-game-found-target"
  | "openlobby-sort-change"
  | "openlobby-player-start"

  // Wiki Article Navigation Events
  | "wiki-article-move"

  // UI Events
  | "global-language-selector"
  | "discord-link-footer";

/**
 * Track an Umami event programmatically
 * @param eventName - The event name to track
 * @param eventData - Optional event data/properties
 */
export function trackEvent(
  eventName: UmamiEvent,
  eventData?: Record<string, unknown>,
): void {
  if (typeof window !== "undefined" && window.umami) {
    window.umami.track(eventName, eventData);
  }
}

/**
 * Set a distinct ID for the current user, associating all subsequent events
 * with this identifier across sessions.
 *
 * The ID has a 50 character limit. If your identifiers are longer,
 * consider using a hash or shortened version.
 *
 * @param data - Object with an `id` property, or any key/value data
 */
export function identify(data: { id: string } | Record<string, unknown>): void {
  if (typeof window !== "undefined" && window.umami) {
    window.umami.identify(data);
  }
}

/**
 * Get data attributes for Umami event tracking
 * Use with spread operator: {...umamiEvent('event-name')}
 *
 * @param eventName - The event name to track
 * @param additionalData - Additional data attributes to include
 * @returns Object with data-umami-event attributes
 */
export function umamiEvent(
  eventName: UmamiEvent,
  additionalData?: Record<string, string>,
): Record<string, string> {
  const attrs: Record<string, string> = {
    "data-umami-event": eventName,
  };

  if (additionalData) {
    Object.entries(additionalData).forEach(([key, value]) => {
      attrs[`data-umami-event-${key}`] = value;
    });
  }

  return attrs;
}
